/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {CommonModule} from '@angular/common';
import {ModuleWithProviders, NgModule} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';

import {TranslateModule} from '@ngx-translate/core';

import {
  BlockingErrorResolutionModalComponent
} from './blocking-error-resolution-modal/blocking-error-resolution-modal.component';
import {ActiveEmployeeFieldComponent} from './fields/active-employee-field.component';
import {BlankFieldComponent} from './fields/blank-field.component';
import {BooleanFieldComponent} from './fields/boolean-field/boolean-field.component';
import {BreakLineFieldComponent} from './fields/break-line-field.component';
import {ContractFieldComponent} from './fields/contract-field/contract-field.component';
import {CurrencyFieldComponent} from './fields/currency-field/currency-field.component';
import {DateFieldComponent} from './fields/date-field/date-field.component';
import {DecimalFieldComponent} from './fields/decimal-field/decimal-field.component';
import {EntityFieldComponent} from './fields/entity-field.component';
import {FileFieldComponent} from './fields/file-field/file-field.component';
import {IntegerFieldComponent} from './fields/integer-field/integer-field.component';
import {ListFieldComponent} from './fields/list-field.component';
import {MonthFieldComponent} from './fields/month-field/month-field.component';
import {PeopleTreeComponent} from './fields/people-tree-field.component';
import {PercentageFieldComponent} from './fields/percentage-field.component';
import {PhotoFieldComponent} from './fields/photo-field.component';
import {FieldRegistry} from './fields/registry';
import {TableFieldComponent} from './fields/table-field.component';
import {TextFieldComponent} from './fields/text-field/text-field.component';
import {TextAreaFieldComponent} from './fields/textarea-field/textarea-field.component';
import {TransIdListFieldComponent} from './fields/trans-id-list-field.component';
import {TreeFieldComponent} from './fields/tree-field/tree-field.component';
import {FormCardFieldComponent} from './form-card/form-card-field/form-card-field.component';
import {FormCardRowComponent} from './form-card/form-card-row/form-card-row.component';
import {FormCardComponent} from './form-card/form-card.component';
import {FormDescriptorService} from './form-descriptor.service';
import {FormFieldMappingIndicatorsComponent} from './form-field-mapping-indicators.component';
import {FormFieldValidationComponent} from './form-field-validation/form-field-validation.component';
import {FormGeneratorService} from './form-generator.service';
import {FormTabRowComponent} from './form-tab/form-tab-row/form-tab-row.component';
import {FormTabComponent} from './form-tab/form-tab.component';
import {FormTableCellComponent} from './form-table/form-table-cell/form-table-cell.component';
import {FormTableRowComponent} from './form-table/form-table-row/form-table-row.component';
import {FormTableComponent} from './form-table/form-table.component';
import {FormTimelineComponent} from './form-timeline/form-timeline.component';
import {BlockingErrorModalComponent} from './generated-form/blocking-error-modal/blocking-error-modal.component';
import {GeneratedFormComponent} from './generated-form/generated-form.component';
import {GeneratedSimpleFormComponent} from './generated-simple-form/generated-simple-form.component';

import {BsDatepickerModule} from 'ngx-bootstrap/datepicker';
import {EmpTreeService} from '../../+employees/emp-tree/emp-tree.service';
import {FilesModule} from '../../shared/files/files.module';
import {ChangeTrackingModule} from '../change-tracking/change-tracking.module';
import {ContractAlertsComponent} from '../contract-alerts/contract-alerts.component';
import {FilterAlertsPipe} from '../contract-alerts/filterAlerts.pipe';
import {DocumentsModule} from '../documents/documents.module';
import {NativeElementInjectorDirective} from '../forms/native-element-injector.directive';
import {LocalizationModule} from '../localization/localization.module';
import {LogEventModule} from '../log-event/log-event.module';
import {ModalModule} from '../modal/modal.module';
import {PipesModule} from '../pipes/pipes.module';
import {SdconnectModule} from '../sdconnect/sdconnect.module';
import {UiModule} from '../ui/ui.module';

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    UiModule,
    TranslateModule,
    PipesModule,
    ChangeTrackingModule,
    DocumentsModule,
    ModalModule,
    LogEventModule,
    SdconnectModule,
    BsDatepickerModule.forRoot(),
    LocalizationModule,
    FilesModule
  ],
  declarations: [
    BooleanFieldComponent,
    BreakLineFieldComponent,
    BlankFieldComponent,
    CurrencyFieldComponent,
    DateFieldComponent,
    MonthFieldComponent,
    EntityFieldComponent,
    ContractFieldComponent,
    TextFieldComponent,
    ListFieldComponent,
    TransIdListFieldComponent,
    PercentageFieldComponent,
    PhotoFieldComponent,
    TextAreaFieldComponent,
    DecimalFieldComponent,
    TreeFieldComponent,
    IntegerFieldComponent,
    FormTabRowComponent,
    FormCardComponent,
    FormCardRowComponent,
    FormCardFieldComponent,
    FormTabComponent,
    GeneratedFormComponent,
    FormTableComponent,
    FormTableCellComponent,
    FormTableRowComponent,
    PeopleTreeComponent,
    TableFieldComponent,
    ActiveEmployeeFieldComponent,
    FormTimelineComponent,
    FileFieldComponent,
    GeneratedSimpleFormComponent,
    BlockingErrorModalComponent,
    FormFieldValidationComponent,
    BlockingErrorResolutionModalComponent,
    NativeElementInjectorDirective,
    ContractAlertsComponent,
    FilterAlertsPipe,
    FormFieldMappingIndicatorsComponent
  ],
  exports: [
    BooleanFieldComponent,
    BreakLineFieldComponent,
    BlankFieldComponent,
    CurrencyFieldComponent,
    DateFieldComponent,
    EntityFieldComponent,
    ContractFieldComponent,
    TextFieldComponent,
    DecimalFieldComponent,
    ListFieldComponent,
    PercentageFieldComponent,
    PhotoFieldComponent,
    TextAreaFieldComponent,
    TreeFieldComponent,
    IntegerFieldComponent,
    FormTabRowComponent,
    FormCardComponent,
    FormCardRowComponent,
    FormCardFieldComponent,
    FormTabComponent,
    GeneratedFormComponent,
    FormTableComponent,
    FormTableCellComponent,
    FormTableRowComponent,
    PeopleTreeComponent,
    TableFieldComponent,
    ActiveEmployeeFieldComponent,
    FormTimelineComponent,
    FileFieldComponent,
    GeneratedSimpleFormComponent,
    BlockingErrorModalComponent,
    BlockingErrorResolutionModalComponent,
    NativeElementInjectorDirective,
    ContractAlertsComponent
  ]
})
export class FormGeneratorModule {
  public static forRoot(): ModuleWithProviders<FormGeneratorModule> {
    return {
      ngModule: FormGeneratorModule,
      providers: [
        FieldRegistry,
        FormGeneratorService,
        FormDescriptorService,
        EmpTreeService
      ]
    };
  }
}
